import { createAsyncThunk } from "@reduxjs/toolkit";

import * as basketAPI from "../../../api/basket";
// Import the concrete module, not the `apps/checkout/thunks` barrel —
// that barrel re-exports this file, so it would create a cycle.
import { loadBasket } from "../../../apps/checkout/thunks/loadBasket";
import { IBasket } from "../../../models/catalogue";
import { IProductAPIURL } from "../../../models/nominals";

/**
 * Arguments for addBasketLines thunk.
 */
interface AddBasketLinesArgs {
    lines: Array<{ productURL: IProductAPIURL; qty?: number }>;
}

/**
 * Add multiple products to the basket.
 *
 * This thunk adds multiple products to the user's basket sequentially,
 * then reloads the basket once to reflect all changes. This is useful
 * for bulk operations like adding gift bundles or promotional sets.
 *
 * @param {Array<{productURL: IProductAPIURL; qty?: number}>} args.lines - Array of products to add with optional quantities
 *
 * @example
 * // Add multiple products at once
 * dispatch(addBasketLines({
 *   lines: [
 *     { productURL: product1.url, qty: 2 },
 *     { productURL: product2.url },
 *   ]
 * }));
 *
 * @sideEffects
 * - Makes sequential API calls to add each product via basketAPI.addProduct
 * - Dispatches loadBasket thunk to reload basket data after all adds
 * - Updates `data.basket` with reloaded basket via extraReducers
 */
// Prefix kept as "checkout/": module only moved, and nothing matches these
// types by string, so renaming would change them unnoticed.
export const addBasketLines = createAsyncThunk(
    "checkout/addBasketLines",
    async (args: AddBasketLinesArgs, { dispatch }): Promise<IBasket | null> => {
        for (const { productURL, qty } of args.lines) {
            await basketAPI.addProduct(productURL, qty);
        }
        const result = await dispatch(loadBasket()).unwrap();
        return result.basket;
    },
);
